import React, { FunctionComponent } from 'react';
import styled from 'styled-components';
import { Form } from 'react-final-form';
import { InputField, Text, Box } from 'mdlkit';
import {
  nameComposed,
  phoneNumberComposed,
  zipCodeComposed,
} from '../../../utils/register/validations';
import {
  normalizeNumberOnlyWithLength,
  normalizeZipCode,
} from '../../../utils/register/normalize';
import { event } from '../../../lib/analytics';
import {
  REGISTRATION,
  CREATE_PROFILE,
  ATTEMPT,
} from '../../../constants/events';
import {
  StyledFlex,
  FormSubmitLoadingButton,
  FocusShadowField,
} from '../../shared/Styles';
import {
  PatientAffiliation,
  PatientSubmitError,
} from '../../../interfaces/register';
import Gender from '../Gender';
import PatientAffiliationList from './PatientAffiliationList';
import toast from '../../../utils/toast';

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
`;

const validateRecord = (values: { gender?: string }) => {
  const errors: { gender?: string } = {};
  if (typeof values.gender === 'undefined') {
    errors.gender = 'Gender required';
  }
  return errors;
};

interface Props {
  onSubmit: (values: any) => Promise<void | PatientSubmitError>;
  affiliations: PatientAffiliation[] | null;
  // PPW-6512 — identity carried from the coverage-check flow. `initialValues` seeds First/Last/
  // Gender/ZIP; when `lockIdentity` is true those fields render read-only (phone stays editable).
  initialValues?: {
    firstName?: string;
    lastName?: string;
    gender?: string;
    zip?: string;
  };
  lockIdentity?: boolean;
}

const ProfileForm: FunctionComponent<Props> = ({
  onSubmit,
  affiliations,
  initialValues,
  lockIdentity = false,
}: Props) => (
  <Form
    onSubmit={onSubmit}
    initialValues={initialValues}
    validate={validateRecord}
    render={({
      handleSubmit,
      submitting,
      pristine,
      hasValidationErrors,
      hasSubmitErrors,
      dirtySinceLastSubmit,
    }) => (
      <StyledForm onSubmit={handleSubmit} spellCheck={false}>
        <Text as="label" pt="md" pb="xs">
          What is your name?
        </Text>
        <StyledFlex>
          <Box>
            <FocusShadowField
              id="firstName"
              label="First Name"
              name="firstName"
              validate={nameComposed}
              component={InputField}
              autoCorrect="off"
              spellCheck={false}
              maxLength={35}
              disabled={lockIdentity}
            />
          </Box>
          <Box>
            <FocusShadowField
              id="lastName"
              label="Last Name"
              name="lastName"
              validate={nameComposed}
              component={InputField}
              autoCorrect="off"
              spellCheck={false}
              maxLength={35}
              disabled={lockIdentity}
            />
          </Box>
        </StyledFlex>

        <Text as="label" htmlFor="gender" pt="md" pb="xs">
          What is your gender?
        </Text>

        <Gender disabled={lockIdentity} />

        <Text as="label" htmlFor="phoneNumber" pt="md" pb="xs">
          Phone Number
        </Text>

        <FocusShadowField
          id="phoneNumber"
          name="phoneNumber"
          inputMode="numeric"
          validate={phoneNumberComposed}
          component={InputField}
          parse={(value: string) => normalizeNumberOnlyWithLength(value, 10)}
          aria-label="phone number"
        />

        <Text as="label" htmlFor="zip" pt="md" pb="xs">
          What&apos;s your home ZIP code?
        </Text>

        <FocusShadowField
          id="zip"
          name="zip"
          inputMode="numeric"
          validate={zipCodeComposed}
          component={InputField}
          parse={normalizeZipCode}
          aria-label="home zip code"
          disabled={lockIdentity}
        />

        {affiliations && <PatientAffiliationList affiliations={affiliations} />}

        <FormSubmitLoadingButton
          id="submit-profile"
          type="submit"
          size="large"
          disabled={
            submitting ||
            pristine ||
            hasValidationErrors ||
            (hasSubmitErrors && !dirtySinceLastSubmit)
          }
          onClick={() => {
            toast.dismiss();
            event({
              action: CREATE_PROFILE,
              category: REGISTRATION,
              parameters: { status: ATTEMPT },
            });
          }}
          isLoading={submitting}
        >
          Submit
        </FormSubmitLoadingButton>
      </StyledForm>
    )}
  />
);

export default ProfileForm;
